import { KatDataTable } from '@amzn/katal-react';
import { TranslateFn } from 'src/i18next-arb-shim/useTranslation';
import { getPhonetoolUrl, ReviewStatus } from '../Utils';

export const CompletedReviewsTableColumn = {
  MESSAGE_GROUP: {
    property: 'stringGroupName',
    translationKey: 'column-source-review-history-message-group',
  },
  REQUEST_ID: {
    property: 'requestId',
    translationKey: 'column-source-review-history-request-id',
  },
  REQUESTED_BY: {
    property: 'sourceReviewRequester',
    translationKey: 'column-source-review-history-requested-by',
  },
  REQUESTED_TIMESTAMP: {
    property: 'sourceReviewRequestedTimestampDisplay',
    translationKey: 'column-source-review-history-requested-on',
  },
  REVIEWED_BY: {
    property: 'updatedBy',
    translationKey: 'column-source-review-history-reviewed-by',
  },
  REVIEWED_TIMESTAMP: {
    property: 'updateTimestampDisplay',
    translationKey: 'column-source-review-history-reviewed-on',
  },
  REVIEW_STATUS: {
    property: 'sourceReviewStatusDisplay',
    translationKey: 'column-source-review-history-status',
  },
} as const;

/**
 * Column configuration for the Completed Reviews table.
 */
export default (t: TranslateFn): KatDataTable.ColumnConfiguration[] => {
  return [
    // Message Group column with link to package permissions for owner information
    {
      property: CompletedReviewsTableColumn.MESSAGE_GROUP.property,
      label: t(CompletedReviewsTableColumn.MESSAGE_GROUP.translationKey),
      sortable: true,
      formatter: (_rowIndex: number, rowData: any) => {
        const element = document.createElement('a');
        const messageGroup =
          rowData[CompletedReviewsTableColumn.MESSAGE_GROUP.property];
        element.href = `https://code.amazon.com/packages/${messageGroup}/permissions`;
        element.textContent = messageGroup;
        element.target = '_blank';
        return { element };
      },
    } as KatDataTable.ColumnConfiguration,
    // Request ID column
    {
      property: CompletedReviewsTableColumn.REQUEST_ID.property,
      label: t(CompletedReviewsTableColumn.REQUEST_ID.translationKey),
      sortable: true,
    } as KatDataTable.ColumnConfiguration,
    // Requested By alias column with link to Phonetool
    {
      property: CompletedReviewsTableColumn.REQUESTED_BY.property,
      label: t(CompletedReviewsTableColumn.REQUESTED_BY.translationKey),
      sortable: true,
      formatter: (_rowIndex: number, rowData: any) => {
        const element = document.createElement('a');
        const requestedBy =
          rowData[CompletedReviewsTableColumn.REQUESTED_BY.property];
        element.href = getPhonetoolUrl(requestedBy);
        element.textContent = requestedBy;
        element.target = '_blank';
        return { element };
      },
    } as KatDataTable.ColumnConfiguration,
    // Requested On column with formatted timestamp
    {
      property: CompletedReviewsTableColumn.REQUESTED_TIMESTAMP.property,
      label: t(CompletedReviewsTableColumn.REQUESTED_TIMESTAMP.translationKey),
      sortable: true,
    } as KatDataTable.ColumnConfiguration,
    // Reviewed By alias column with link to Phonetool
    {
      property: CompletedReviewsTableColumn.REVIEWED_BY.property,
      label: t(CompletedReviewsTableColumn.REVIEWED_BY.translationKey),
      sortable: true,
      formatter: (_rowIndex: number, rowData: any) => {
        const element = document.createElement('a');
        const reviewedBy =
          rowData[CompletedReviewsTableColumn.REVIEWED_BY.property];
        element.href = getPhonetoolUrl(reviewedBy);
        element.textContent = reviewedBy;
        element.target = '_blank';
        return { element };
      },
    } as KatDataTable.ColumnConfiguration,
    // Reviewed On column with formatted timestamp
    {
      property: CompletedReviewsTableColumn.REVIEWED_TIMESTAMP.property,
      label: t(CompletedReviewsTableColumn.REVIEWED_TIMESTAMP.translationKey),
      sortable: true,
    } as KatDataTable.ColumnConfiguration,
    // Review Status column with color-coded status display
    {
      property: CompletedReviewsTableColumn.REVIEW_STATUS.property,
      label: t(CompletedReviewsTableColumn.REVIEW_STATUS.translationKey),
      sortable: true,
      formatter: (_rowIndex: number, rowData: any) => {
        const element = document.createElement('span');
        const status =
          rowData[CompletedReviewsTableColumn.REVIEW_STATUS.property];
        if (status === ReviewStatus.APPROVED) {
          element.textContent = t('source-review-status-approved');
          element.classList.add('lui-status-green');
        } else if (status === ReviewStatus.REJECTED) {
          element.textContent = t('source-review-status-rejected');
          element.classList.add('lui-status-red');
        } else {
          element.textContent = status;
        }
        return { element };
      },
    } as KatDataTable.ColumnConfiguration,
  ];
};
